import app from 'flarum/forum/app';

export interface BadgeCounts {
  all: number;
  featured: number;
  following: number;
  tags: Record<string, number>;
}

/**
 * Badge计数管理器
 * 负责获取和缓存未读数据
 */
class BadgeManager {
  private counts: BadgeCounts | null = null;
  private loading = false;
  private callbacks: Array<(counts: BadgeCounts) => void> = [];
  private updateListeners: Array<(counts: BadgeCounts) => void> = [];

  /**
   * 获取Badge计数
   */
  async getCounts(force = false): Promise<BadgeCounts> {
    if (!app.session.user) {
      return { all: 0, featured: 0, following: 0, tags: {} };
    }

    if (this.counts && !force) {
      return this.counts;
    }

    if (this.loading) {
      return new Promise((resolve) => {
        this.callbacks.push(resolve);
      });
    }

    this.loading = true;

    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/tanka-badges',
      });

      this.counts = response.attributes;
      this.loading = false;

      // 执行所有等待的回调
      this.callbacks.forEach(callback => callback(this.counts!));
      this.callbacks = [];

      // 触发更新事件，通知其他组件更新
      this.notifyListeners(this.counts);

      return this.counts;
    } catch (error) {
      this.loading = false;
      console.error('Failed to fetch badge counts:', error);
      
      // 返回默认值
      const defaultCounts = { all: 0, featured: 0, following: 0, tags: {} };
      this.callbacks.forEach(callback => callback(defaultCounts));
      this.callbacks = [];
      
      return defaultCounts;
    }
  }

  /**
   * 刷新Badge计数
   */
  async refresh(): Promise<BadgeCounts> {
    return this.getCounts(true);
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    this.counts = null;
  }

  /**
   * 获取缓存的计数（不发起网络请求）
   */
  getCachedCounts(): BadgeCounts | null {
    return this.counts;
  }

  /**
   * 通知所有监听器
   */
  private notifyListeners(counts: BadgeCounts): void {
    this.updateListeners.forEach(listener => {
      try {
        listener(counts);
      } catch (error) {
        console.error('Error in badge update listener:', error);
      }
    });
  }

  /**
   * 监听Badge计数更新
   */
  onUpdate(callback: (counts: BadgeCounts) => void): void {
    this.updateListeners.push(callback);
  }

  /**
   * 取消监听Badge计数更新
   */
  offUpdate(callback: (counts: BadgeCounts) => void): void {
    const index = this.updateListeners.indexOf(callback);
    if (index > -1) {
      this.updateListeners.splice(index, 1);
    }
  }
}

export default new BadgeManager();
